import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { Document } from 'react-pdf';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import { ReactComponent as TextHighlightIcon } from '@assets/icons/ShapesDrawingTools/text_highlight.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as CheckSvg } from '@assets/icons/check.svg';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import 'react-pdf/dist/Page/TextLayer.css';
import { getInitialPdfPageScale } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import PdfPage from '@pages/MediaViewer/AssetViewer/PdfCard/components/PdfPage';
import type { PdfCardProps } from '@pages/MediaViewer/AssetViewer/PdfCard/PdfCard';
import { usePdfFabricCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfFabricCanvas';
import { usePdfScroll } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfScroll';
import {
  PdfPinchGestureState,
  useMobilePdfPinchZoom
} from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfPinchZoom';
import { useMobilePdfZoomOption } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfZoomOption';
import { useMobilePdfTextHighlight } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/useMobilePdfTextHighlight';
import {
  isAnnotationDrawTool,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';

const maxCanvasSize = 2048;
const maxZoom = 3;

export default memo(function PdfCardMobile({
  onReady,
  asset,
  isAssetReady
}: PdfCardProps) {
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    canvasMode,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setZoomFactor,
    setZoomOption,
    setCanvasMode,
    isMobileDrawingToolbarOpen,
    setDrawToolsShape,
    setIsCommentFieldFocused,
    setActivePageHasText
  } = useAssetViewerState();
  const isShapeDrawing =
    shapesDrawingTools.shape !== CURSOR_DRAW_TOOL && isMobileDrawingToolbarOpen;
  const isTextHighlightMode = shapesDrawingTools.shape === 'documentHighlight';
  const isMainCanvasVisible =
    !!selectedCommentId ||
    (isAnnotationDrawTool(shapesDrawingTools.shape) &&
      canvasMode === 'edit' &&
      isMobileDrawingToolbarOpen);

  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const pageRefsMap = useRef(new Map<number, HTMLDivElement>());
  const pdfPinchGestureRef = useRef<PdfPinchGestureState>({
    isActive: false,
    startSpan: 0,
    baseZoom: 1,
    anchorPage: 0,
    anchorRatioX: 0,
    anchorRatioY: 0,
    anchorViewportOffsetX: 0,
    anchorViewportOffsetY: 0,
    anchorContentX: 0,
    anchorContentY: 0
  });
  const fitZoomRef = useRef(zoomFactor);
  const skipActivePageScrollSyncRef = useRef(false);
  const isLoadPdf = useRef<boolean>(false);
  const mobilePagesReadyRef = useRef(new Set<number>());
  const pageHasTextRef = useRef(new Map<number, boolean>());

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const [pagesCount, setPagesCount] = useState(1);
  const [pageRefsVersion, setPageRefsVersion] = useState(0);
  const [mobilePagesReadyVersion, setMobilePagesReadyVersion] = useState(0);
  const [pdfScrollViewport, setPdfScrollViewport] = useState<Element | null>(
    null
  );
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });

  useEffect(() => {
    isLoadPdf.current = false;
    mobilePagesReadyRef.current.clear();
    pageHasTextRef.current.clear();
    setActivePageHasText(true);
    setIsPageSizeReady(false);
  }, [asset.id, setActivePageHasText]);

  const _pageSize = useMemo(() => {
    return {
      width: pageSize.width * zoomFactor,
      height: pageSize.height * zoomFactor
    };
  }, [pageSize, zoomFactor]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  const rowPitch = useMemo(() => {
    const height = pageSize.height * zoomFactor;
    return height > 0 ? height + 8 : 0;
  }, [pageSize.height, zoomFactor]);

  const onRegisterMobilePageRoot = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const hadElement = pageRefsMap.current.has(page);
      if (element) {
        pageRefsMap.current.set(page, element);
        if (!hadElement) setPageRefsVersion((value) => value + 1);
        return;
      }
      pageRefsMap.current.delete(page);
      if (hadElement) setPageRefsVersion((value) => value + 1);
    },
    []
  );

  const onMobilePageLoadSuccess = useCallback((page: number) => {
    if (mobilePagesReadyRef.current.has(page)) return;
    mobilePagesReadyRef.current.add(page);
    setMobilePagesReadyVersion((value) => value + 1);
  }, []);

  const onMobilePageTextContent = useCallback(
    (page: number, hasText: boolean) => {
      pageHasTextRef.current.set(page, hasText);
      if (page === activePageNumber) setActivePageHasText(hasText);
    },
    [activePageNumber, setActivePageHasText]
  );

  useEffect(() => {
    setActivePageHasText(
      pageHasTextRef.current.get(activePageNumber) !== false
    );
  }, [activePageNumber, setActivePageHasText]);

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      const viewport =
        wrapperRef.current?.osInstance()?.elements()?.viewport ?? null;
      setPdfScrollViewport(viewport);
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [isAssetReady, pagesCount]);

  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      setPageSize(
        getInitialPdfPageScale(
          originalWidth,
          originalHeight,
          maxCanvasSize,
          maxZoom,
          true
        )
      );
      setIsPageSizeReady(true);
    },
    []
  );

  const { runCanvasLayout, onClearHighlightObjects, renderTextHighlights } =
    usePdfFabricCanvas({
      fabricContext,
      wrapperRef,
      contentRef,
      canvasContainerRef,
      pageRefsMap,
      activePageNumber,
      zoomFactor,
      isPageSizeReady,
      selectedCommentId,
      pageRefsVersion,
      pagesReadyVersion: mobilePagesReadyVersion,
      isMainCanvasVisible,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    });

  const { renderRange, visiblePageNumber, updateActivePageFromScroll } =
    usePdfScroll({
      wrapperRef,
      pageRefsMap,
      skipActivePageScrollSyncRef,
      pagesArray,
      pagesCount,
      rowPitch,
      isPageSizeReady,
      pageRefsVersion,
      activePageNumber,
      selectedCommentId,
      shapesDrawingTools,
      runCanvasLayout,
      setActivePageNumber,
      selectComment,
      setDocumentHighlight,
      setCanvasMode
    });

  useMobilePdfPinchZoom({
    pdfScrollViewport,
    wrapperRef,
    contentRef,
    pageRefsMap,
    pdfPinchGestureRef,
    fitZoomRef,
    skipActivePageScrollSyncRef,
    zoomFactor,
    setZoomFactor,
    updateActivePageFromScroll
  });

  useMobilePdfZoomOption({
    wrapperRef,
    pdfPinchGestureRef,
    fitZoomRef,
    pageSize,
    turnoverDegree,
    isPageSizeReady,
    zoomOption,
    setZoomFactor,
    setZoomOption
  });

  const { selectedTextLength, onCancelTextHighlight, onConfirmTextHighlight } =
    useMobilePdfTextHighlight({
      pageRefsMap,
      pageCanvasRef,
      pdfPinchGestureRef,
      isShapeDrawing,
      shapesDrawingTools,
      selectedCommentId,
      activePageNumber,
      documentHighlight,
      zoomFactor,
      onClearHighlightObjects,
      renderTextHighlights,
      setDocumentHighlight,
      setDrawToolsShape,
      setIsCommentFieldFocused
    });

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing,
        'b-shapes-drawing': isShapeDrawing
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
      }}
      loading={<PlaybackPreparingLoader />}
    >
      <div className="media_viewer_pdf-page-indicator">
        {visiblePageNumber} of {pagesCount}
      </div>
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div
          className={classNames(
            'media_viewer_pdf-content',
            'media_viewer_pdf-content-mobile'
          )}
          ref={contentRef}
        >
          <div
            className="media_viewer_pdf-mobile-pages"
            onContextMenu={
              isTextHighlightMode ? (e) => e.preventDefault() : undefined
            }
          >
            {pagesArray.map(({ page }) => (
              <PdfPage
                key={page}
                pageNumber={page}
                pageWidth={_pageSize.width}
                pageHeight={_pageSize.height}
                zoomFactor={zoomFactor}
                turnoverDegree={turnoverDegree}
                isActive={page === activePageNumber}
                isMainCanvasVisible={isMainCanvasVisible}
                isFirstPage={page === 1}
                shouldRender={
                  page >= renderRange.start && page <= renderRange.end
                }
                pageCanvasRef={pageCanvasRef}
                onRegisterPageRoot={onRegisterMobilePageRoot}
                onFirstPageLoadSuccess={onLoadSuccess}
                onPageLoadSuccess={onMobilePageLoadSuccess}
                onPageTextContent={onMobilePageTextContent}
              />
            ))}
          </div>
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {(!isAssetReady || !isPageSizeReady) && <PlaybackPreparingLoader />}
      {isTextHighlightMode && (
        <div className="media_viewer_pdf-text-highlight-mobile">
          <div className="media_viewer_pdf-text-highlight-mobile-icon">
            <TextHighlightIcon />
          </div>
          <div className="media_viewer_pdf-text-highlight-mobile-content">
            {selectedTextLength
              ? `${selectedTextLength} characters selected`
              : 'Select text to attach'}
          </div>
          <div className="media_viewer_pdf-text-highlight-mobile-btn-container">
            <div
              className="media_viewer_pdf-text-highlight-mobile-btn"
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCancelTextHighlight();
              }}
            >
              <CrossSvg width={20} height={20} />
            </div>
            {!!selectedTextLength && (
              <div
                className="media_viewer_pdf-text-highlight-mobile-btn"
                onPointerDown={onConfirmTextHighlight}
              >
                <CheckSvg width={14} height={14} />
              </div>
            )}
          </div>
        </div>
      )}
    </Document>
  );
});
